import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CLINIC_PHONE, buildWhatsAppUrl } from '../data/clinicContact';
import { emergencyFaqs } from '../data/emergencyDentistCusco';
import { useBookAppointmentModal } from '../context/BookAppointmentModalContext';
import { useLocale } from '../context/LocaleContext';
import { paths } from '../routes/paths';
import { orthoPagePath } from '../data/orthoMenuPages';
import LowerFooter from '../Components/LowerFooter';
import sensibilidadImg from './images/sensibilidad-dental.png';
import './LandingPages.css';

const WHATSAPP_HREF = buildWhatsAppUrl(
  'Hi, I am a tourist in Cusco and I need an emergency dentist. I have dental pain / a broken tooth.'
);

const MAP_EMBED_SRC =
  'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2494.2481953242086!2d-71.96398252281416!3d-13.522669105093586!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x916dd5fd6e89d925%3A0x5028062beeb7ea6f!2sClinica%20Dental%20Smile%20Up%20%7C%20Ortodoncia%20%2C%20Implante%20%2C%20Odontopediatria.!5e0!3m2!1sen!2spe!4v1769210347450!5m2!1sen!2spe';

const MAP_DIRECTIONS_URL = 'https://maps.app.goo.gl/ZcuCg67p7StV3LBU7';

const EMERGENCY_CASES = [
  {
    title: 'Severe toothache',
    text: 'Pain that does not settle, wakes you at night, or worsens with cold, heat or chewing often needs same-day evaluation — especially before a trek, bus or flight.',
  },
  {
    title: 'Broken or cracked tooth',
    text: 'A chip from hiking, dining or a fall can leave sharp edges and expose the tooth. Early care improves the chance of saving it and keeping you comfortable on the trip.',
  },
  {
    title: 'Lost filling, crown or temporary',
    text: 'Exposed dentin can become very sensitive at altitude and with cold drinks. We can protect the tooth so you can keep eating and continue your itinerary.',
  },
  {
    title: 'Swelling or dental infection',
    text: 'Gum or facial swelling, pus or fever are red flags. Do not wait if infection is possible — especially before long travel days.',
  },
  {
    title: 'Braces or aligner emergency',
    text: 'A poking wire, loose bracket or lost aligner can ruin a Machu Picchu day. We can stabilize the appliance and advise your home orthodontist.',
  },
  {
    title: 'Trauma after a fall',
    text: 'Knocks to the mouth on stairs, trails or in traffic need a prompt check of teeth, bite and soft tissue before swelling hides the damage.',
  },
];

function Ctas({ primaryLabel = 'Book urgent appointment' }) {
  const { openBookAppointmentModal } = useBookAppointmentModal();

  return (
    <div className="landing_ctas">
      <button
        type="button"
        className="landing_primary"
        onClick={openBookAppointmentModal}
      >
        {primaryLabel}
      </button>
      <a
        className="landing_secondary"
        href={WHATSAPP_HREF}
        target="_blank"
        rel="noopener noreferrer"
      >
        WhatsApp us
      </a>
      <a className="landing_secondary" href={CLINIC_PHONE.href}>
        Call {CLINIC_PHONE.label}
      </a>
    </div>
  );
}

export default function LandingEmergencyCusco() {
  const { locale, setLocale } = useLocale();

  useEffect(() => {
    if (locale !== 'en') setLocale('en');
  }, [locale, setLocale]);

  return (
    <div className="landing_page landing_page--emergency">
      <header className="landing_hero landing_hero_emergency landing_hero--split">
        <div className="landing_inner landing_hero_inner">
          <div className="landing_hero_text">
            <nav className="landing_crumbs" aria-label="Breadcrumb">
              <ol>
                <li>
                  <Link to="/">Home</Link>
                </li>
                <li aria-current="page">Emergency dentist in Cusco</li>
              </ol>
            </nav>
            <p className="landing_eyebrow">English-speaking dental clinic · Cusco, Peru</p>
            <h1 className="landing_h1">Emergency dentist in Cusco, Peru</h1>
            <p className="landing_sub">
              Need a dentist in Cusco today? Smile Up provides same-day emergency
              dental care for tourists and travelers: toothache, broken tooth,
              swelling, lost filling or braces problems — explained in clear
              English at Av. La Cultura 822, Wanchaq.
            </p>
            <Ctas />
            <div className="landing_trust">
              <span>English &amp; Spanish</span>
              <span className="landing_dot">•</span>
              <span>Same-day options when available</span>
              <span className="landing_dot">•</span>
              <span>Av. La Cultura 822, Wanchaq</span>
            </div>
          </div>
          <div className="landing_hero_media">
            <img
              className="landing_hero_img"
              src={sensibilidadImg}
              alt="Patient with tooth pain visiting an emergency dentist in Cusco at Smile Up"
              width={640}
              height={480}
            />
          </div>
        </div>
      </header>

      <main className="landing_inner landing_main">
        <section className="landing_section">
          <h2 className="landing_h2">Dental emergency care for tourists in Cusco</h2>
          <p className="landing_p">
            A toothache in Cusco can stop a trip fast. High altitude, cold drinks,
            long walks and delayed meals often make an existing dental problem feel
            suddenly urgent. At Smile Up we see international visitors who need an{' '}
            <strong>emergency dentist in Cusco</strong> without a long wait or a
            language barrier.
          </p>
          <p className="landing_p landing_p--spaced">
            Our goal is practical: relieve pain, protect the tooth, and tell you
            honestly what must be done now versus what can wait until you see your
            dentist at home. Dr. Germán Paniura and the clinical team also help
            travelers with urgent braces or aligner issues.
          </p>
        </section>

        <section className="landing_section">
          <h2 className="landing_h2">When to see an emergency dentist in Cusco</h2>
          <p className="landing_p">
            Book urgent dental care if you have any of the following while
            traveling in Cusco or the Sacred Valley:
          </p>
          <ul className="landing_list">
            <li>Persistent toothache, night pain or pain that is getting worse</li>
            <li>A broken, cracked or knocked tooth</li>
            <li>A filling, crown, veneer or temporary that came out</li>
            <li>Swelling of the gum, cheek or face</li>
            <li>A poking orthodontic wire, loose bracket or lost aligner</li>
            <li>Bleeding that does not stop after a knock to the mouth</li>
          </ul>
          <div className="landing_note landing_note--alert">
            <strong>Go to a hospital first</strong> if you have trouble breathing
            or swallowing, rapidly spreading facial swelling, uncontrolled
            bleeding, or head trauma with dizziness. Those are medical
            emergencies.
          </div>
        </section>

        <section className="landing_section">
          <h2 className="landing_h2">Common tourist dental emergencies</h2>
          <div className="landing_cards">
            {EMERGENCY_CASES.map((item) => (
              <article className="landing_card" key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="landing_section">
          <h2 className="landing_h2">How a same-day dental visit works</h2>
          <div className="landing_steps">
            <div className="landing_step">
              <div className="landing_step_num">1</div>
              <div>
                <h3>Tell us what happened</h3>
                <p>
                  Book online or WhatsApp a short description and a photo. We
                  prioritize pain, swelling and trauma.
                </p>
              </div>
            </div>
            <div className="landing_step">
              <div className="landing_step_num">2</div>
              <div>
                <h3>Exam and imaging</h3>
                <p>
                  We check the tooth, bite and gums. An X-ray may be needed to
                  see infection, cracks or a lost restoration.
                </p>
              </div>
            </div>
            <div className="landing_step">
              <div className="landing_step_num">3</div>
              <div>
                <h3>Stabilize so you can travel</h3>
                <p>
                  Treatment is aimed at comfort and safety: pain relief,
                  temporary coverage, infection control or appliance repair —
                  with notes for your dentist abroad.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="landing_section">
          <h2 className="landing_h2">What to do before you arrive</h2>
          <ul className="landing_list">
            <li>Rinse gently with warm water and keep the area clean.</li>
            <li>Use a cold compress for swelling (10–15 minutes on/off).</li>
            <li>Avoid very hot, cold, sticky or hard foods if the tooth is sensitive.</li>
            <li>Do not place aspirin on the gum — it can burn the tissue.</li>
            <li>
              Save a broken fragment or a knocked-out temporary in a clean
              container if you still have it.
            </li>
            <li>
              Message us on WhatsApp with a photo — we will tell you how urgent
              the visit is.
            </li>
          </ul>
        </section>

        <section className="landing_section landing_split">
          <div>
            <h2 className="landing_h2">English-speaking dentist for travelers</h2>
            <p className="landing_p">
              Many visitors search for an English-speaking dentist in Cusco after
              a night of pain. We explain options in plain language, including
              what a temporary repair can achieve before you fly to Lima, the
              United States, Europe or onward in South America.
            </p>
            <p className="landing_p landing_p--spaced">
              If you are already in orthodontic treatment, we can help with
              urgent braces or aligner problems and coordinate with your home
              orthodontist. Learn more about{' '}
              <Link to={orthoPagePath('whyInternational')}>
                care for international patients
              </Link>{' '}
              and our{' '}
              <Link to={paths.location}>clinic location in Wanchaq</Link>.
            </p>
            <Ctas primaryLabel="Book now" />
          </div>
          <div className="landing_hours_box">
            <h2 className="landing_h2">Hours &amp; contact</h2>
            <dl className="landing_hours">
              <div>
                <dt>Monday – Friday</dt>
                <dd>10:00–19:00</dd>
              </div>
              <div>
                <dt>Saturday</dt>
                <dd>9:00–14:00</dd>
              </div>
              <div>
                <dt>Sunday</dt>
                <dd>Closed</dd>
              </div>
            </dl>
            <address className="landing_nap">
              Smile Up
              <br />
              Av. La Cultura 822, Wanchaq
              <br />
              Cusco, Peru
              <br />
              <a href={CLINIC_PHONE.href}>{CLINIC_PHONE.label}</a>
            </address>
            <p className="landing_p landing_p--small">
              After hours: if swelling affects breathing or swallowing, go to a
              hospital emergency department.
            </p>
          </div>
        </section>

        <section className="landing_section">
          <h2 className="landing_h2">How to get to the clinic</h2>
          <p className="landing_p">
            Av. La Cultura is one of the main avenues in Cusco. From Plaza de
            Armas or San Blas, a taxi or ride-hail to Wanchaq usually takes
            10–20 minutes depending on traffic. Ask for Smile Up, Av. La Cultura
            822.
          </p>
          <div className="landing_map_wrap">
            <iframe
              title="Map of Smile Up emergency dentist in Cusco"
              className="landing_map_iframe"
              src={MAP_EMBED_SRC}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
          <div className="landing_ctas">
            <a
              className="landing_secondary"
              href={MAP_DIRECTIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Google Maps
            </a>
            <Link className="landing_secondary_link" to={paths.location}>
              Full location page
            </Link>
          </div>
        </section>

        <section className="landing_section landing_faq" id="faq">
          <h2 className="landing_h2">FAQ: emergency dentist Cusco</h2>
          {emergencyFaqs.map((item) => (
            <details key={item.q}>
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>
          ))}
        </section>

        <section className="landing_bottom_cta">
          <h2 className="landing_h2">Need an emergency dentist in Cusco today?</h2>
          <p className="landing_p">
            Book online, WhatsApp or call. Early care helps you stay on your
            travel plans — whether that is Machu Picchu, the Sacred Valley or
            your flight home.
          </p>
          <Ctas primaryLabel="Book urgent appointment" />
          <p className="landing_disclaimer">
            This page is general information for travelers, not a personal
            diagnosis. Treatment is decided after an in-person exam.
          </p>
        </section>
      </main>

      <LowerFooter />
    </div>
  );
}
